import React from 'react';

import type { Transaction } from 'types/api/transaction';

import fetchTxRevertMessage from 'lib/tx/fetchTxRevertMessage';
import getTxErrorText from 'lib/tx/getTxErrorText';

/**
 * Resolves a human-readable failure reason:
 * 1) indexed revert_reason / result via getTxErrorText
 * 2) RPC eth_call recovery when indexer still has awaiting_internal_transactions
 */
export default function useResolvedTxErrorText(tx: Pick<Transaction, 'hash' | 'status' | 'result' | 'revert_reason'> | undefined) {
  const base = tx ? getTxErrorText(tx) : undefined;
  const needsRpc = Boolean(
    tx &&
    tx.status === 'error' &&
    !tx.revert_reason &&
    (!tx.result || /awaiting_internal_transactions|error|failed/i.test(tx.result)),
  );

  const [ rpcReason, setRpcReason ] = React.useState<string | undefined>();

  React.useEffect(() => {
    let cancelled = false;
    setRpcReason(undefined);

    if (!tx?.hash || !needsRpc) {
      return;
    }

    fetchTxRevertMessage(tx.hash).then((reason) => {
      if (!cancelled && reason) {
        setRpcReason(reason);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ tx?.hash, needsRpc ]);

  if (!tx || tx.status !== 'error') {
    return undefined;
  }

  // Prefer concrete RPC/decoded message over generic indexer placeholders
  if (rpcReason) {
    return rpcReason;
  }

  return base;
}
